<script>
  import { onMount } from "svelte";
  import { fade, slide } from "svelte/transition";
  import { activeTab, selectedQuoteForJob, customerQuote, selectedJobForEdit, selectedJobs } from "../../stores";
  import { fetchQuotes, fetchTrucks, fetchJobs, generateJobFromQuote, updateJob, createJob, updateQuoteDoc, fetchCompanies, fetchYards, deleteJob, activateJobLive } from "../../data";
  import TruckAllocationBoard from "./TruckAllocationBoard.svelte";
  import CreateJobSidebar from "./CreateJobSidebar.svelte";
  import StartJobModal from "./StartJobModal.svelte";
  import DeleteJobModal from "./DeleteJobModal.svelte";
  import {
    buildJobMaterialsPayload,
    mapAllocationsToAssignedTrucks,
    formatCurrency,
    formatTonnes,
    getQuoteCustomerName,
    getQuoteSiteAddress,
    getQuoteTotalTonnes,
    getQuoteGrandTotal,
    getJobCustomerName as getJobCustomerNameUtil,
    getJobSiteAddress,
    getJobTotalTonnes,
    getJobTotalAllocatedTrucks
  } from "./jobMaterialUtils.js";
  import Checkmark from "carbon-icons-svelte/lib/Checkmark.svelte";
  import Renew from "carbon-icons-svelte/lib/Renew.svelte";
  import ArrowRight from "carbon-icons-svelte/lib/ArrowRight.svelte";
  import DeliveryTruck from "carbon-icons-svelte/lib/DeliveryTruck.svelte";
  import DocumentAdd from "carbon-icons-svelte/lib/DocumentAdd.svelte";
  import WarningFilled from "carbon-icons-svelte/lib/WarningFilled.svelte";
  import Calendar from "carbon-icons-svelte/lib/Calendar.svelte";
  import Close from "carbon-icons-svelte/lib/Close.svelte";
  import SidePanelClose from "carbon-icons-svelte/lib/SidePanelClose.svelte";
  import SidePanelOpen from "carbon-icons-svelte/lib/SidePanelOpen.svelte";
  import Add from "carbon-icons-svelte/lib/Add.svelte";
  import Save from "carbon-icons-svelte/lib/Save.svelte";
  import PlayFilled from "carbon-icons-svelte/lib/PlayFilled.svelte";
  import TrashCan from "carbon-icons-svelte/lib/TrashCan.svelte";
  import Email from "carbon-icons-svelte/lib/Email.svelte";
  import Phone from "carbon-icons-svelte/lib/Phone.svelte";
  import ChevronLeft from "carbon-icons-svelte/lib/ChevronLeft.svelte";
  import ChevronRight from "carbon-icons-svelte/lib/ChevronRight.svelte";
  import PhoneInput from "../../components/PhoneInput.svelte";
  import { formatPhoneNumber, toE164 } from "../../phoneUtils.js";
  import { loadQuoteIntoCustomerQuoteStore, fetchAndLoadQuote } from "../../quoteUtils.js";

  let quotes = [];
  let jobsList = [];
  let isLoadingQuotes = false;
  let isLoadingJobs = false;
  let quotesError = "";
  let jobsError = "";

  function getJobCustomerName(job) {
    return getJobCustomerNameUtil(job, selectedQuote, quotes);
  }

  let isSidebarOpen = true;
  let isFleetDrawerOpen = false;
  let fleetTrucks = [];

  // Creation mode switcher
  let creationMode = "quote"; // 'quote' | 'manual'
  let manualJob = {
    companyName: "",
    street: "",
    suburb: "",
    state: "NSW",
    postcode: "",
    contactName: "",
    contactPhone: "",
    status: "active",
    poNumber: "",
    startDate: new Date().toISOString().split("T")[0],
    daysCount: 1,
    startTime: "07:00",
    finishTime: "17:00",
    loadInterval: 15,
    loadingUhf: "29",
    disposalUhf: "14",
    materialName: "VENM Sand & Clean Fill",
    materialAmount: 500,
    siteNotes: ""
  };
  let isCreatingManual = false;
  let manualCreateError = "";
  let manualCreateSuccess = null;

  async function handleCreateManualJob() {
    if (!manualJob.companyName.trim()) {
      manualCreateError = "Client / Company name is required.";
      return;
    }
    isCreatingManual = true;
    manualCreateError = "";
    manualCreateSuccess = null;
    try {
      const clientPhone = manualJob.contactPhone.trim() ? toE164(manualJob.contactPhone.trim()) : "";
      const clientAddress = {
        street: manualJob.street.trim(),
        suburb: manualJob.suburb.trim(),
        state: manualJob.state.trim() || "NSW",
        postcode: manualJob.postcode.trim()
      };
      const clientObj = {
        name: manualJob.companyName.trim(),
        phone: clientPhone,
        address: clientAddress
      };
      const tonnage = parseFloat(manualJob.materialAmount) || 500;
      const totalPayload = 38;
      const totalLoads = Math.ceil(tonnage / totalPayload);
      const runsPerDay = 5;
      const estimatedDays = Number((totalLoads / runsPerDay).toFixed(2));

      const payload = {
        customer: clientObj,
        address: clientAddress,
        contacts: [
          {
            name: manualJob.contactName.trim() || "Site Supervisor",
            phone: clientPhone,
            position: "Site Contact",
            main: true
          }
        ],
        status: manualJob.status,
        po_number: manualJob.poNumber.trim(),
        start_date: manualJob.startDate,
        days_count: parseInt(manualJob.daysCount, 10) || 1,
        load_interval: parseInt(manualJob.loadInterval, 10) || 15,
        load_uhf_channel: manualJob.loadingUhf.trim(),
        dispose_uhf_channel: manualJob.disposalUhf.trim(),
        site_notes: manualJob.siteNotes.trim(),
        notes: manualJob.siteNotes.trim(),
        materials: [
          {
            name: manualJob.materialName.trim() || "Bulk Material",
            amount: tonnage,
            status: manualJob.status,
            start_date: manualJob.startDate,
            days_count: parseInt(manualJob.daysCount, 10) || 1,
            load_interval: parseInt(manualJob.loadInterval, 10) || 15,
            load_uhf_channel: manualJob.loadingUhf.trim(),
            dispose_uhf_channel: manualJob.disposalUhf.trim(),
            site_notes: manualJob.siteNotes.trim(),
            notes: manualJob.siteNotes.trim(),
            yard: null,
            load_address: {
              street: manualJob.street.trim(),
              suburb: manualJob.suburb.trim(),
              state: manualJob.state.trim() || "NSW"
            },
            operating_hours: {
              start_time: manualJob.startTime || "07:00",
              finish_time: manualJob.finishTime || "17:00",
              load_mins: 15,
              tip_mins: 15
            },
            fleet: {
              truck_type: "10 wheeler - 19t",
              truck_capacity_tonne: 19,
              trailer_type: "8 wheeler - 20t",
              trailer_capacity_tonne: 19,
              total_capacity_tonne: 38,
              truck_day_rate: 1400
            },
            schedule: {
              runs_per_day: runsPerDay,
              total_loads: totalLoads,
              allocated_trucks: 1,
              estimated_days: estimatedDays,
              total_tolls: 0
            }
          }
        ]
      };

      const created = await createJob(payload);
      manualCreateSuccess = created;
      const mSt = (manualJob.status || "active").toLowerCase();
      if (!selectedStatuses.includes(mSt)) {
        selectedStatuses = [...selectedStatuses, mSt];
      }
      await loadJobs();
    } catch (err) {
      console.error("Failed to create manual job:", err);
      manualCreateError = err.message || "Failed to create job.";
    } finally {
      isCreatingManual = false;
    }
  }

  function toggleSidebar() {
    isSidebarOpen = !isSidebarOpen;
  }

  let selectedQuote = null;
  let selectedMaterialForAllocation = null;
  let currentAllocations = {};
  let currentJob = null;
  let autoSaveTimeout = null;

  $: activeMatForSchedule = selectedMaterialForAllocation || (selectedQuote?.quote_materials && selectedQuote.quote_materials[0]) || null;

  function handleTurnaroundScheduleChanged(e) {
    const updatedMat = e.detail?.material || activeMatForSchedule;
    if (!updatedMat) return;

    if (selectedQuote && Array.isArray(selectedQuote.quote_materials)) {
      const idx = selectedQuote.quote_materials.findIndex(m => 
        (m.id && m.id === updatedMat.id) || 
        (m.material_name && m.material_name === (updatedMat.material_name || updatedMat.name))
      );
      if (idx !== -1) {
        selectedQuote.quote_materials[idx] = { 
          ...selectedQuote.quote_materials[idx], 
          ...updatedMat,
          total_tonnes: updatedMat.amount != null ? updatedMat.amount : updatedMat.total_tonnes,
          amount: updatedMat.amount != null ? updatedMat.amount : updatedMat.total_tonnes,
          fleet: updatedMat.fleet,
          schedule: updatedMat.schedule,
          operating_hours: updatedMat.operating_hours
        };
        delete selectedQuote.quote_materials[idx].total_loads;
        delete selectedQuote.quote_materials[idx].runs_per_day;
        delete selectedQuote.quote_materials[idx].estimated_days;
        delete selectedQuote.quote_materials[idx].truck_day_rate;
        delete selectedQuote.quote_materials[idx].day_rate;
        delete selectedQuote.quote_materials[idx].total_tolls;
        delete selectedQuote.quote_materials[idx].load_mins;
        delete selectedQuote.quote_materials[idx].tip_mins;
        delete selectedQuote.quote_materials[idx].start_time;
        delete selectedQuote.quote_materials[idx].finish_time;
        selectedQuote = { ...selectedQuote };
      }
    }
    if (currentJob && Array.isArray(currentJob.materials)) {
      const jIdx = currentJob.materials.findIndex(m => 
        (m.id && m.id === updatedMat.id) || 
        (m.name && m.name === (updatedMat.material_name || updatedMat.name))
      );
      if (jIdx !== -1) {
        currentJob.materials[jIdx] = { 
          ...currentJob.materials[jIdx], 
          ...updatedMat,
          amount: updatedMat.amount != null ? updatedMat.amount : updatedMat.total_tonnes,
          fleet: updatedMat.fleet,
          schedule: updatedMat.schedule,
          operating_hours: updatedMat.operating_hours
        };
        delete currentJob.materials[jIdx].total_loads;
        delete currentJob.materials[jIdx].runs_per_day;
        delete currentJob.materials[jIdx].estimated_days;
        delete currentJob.materials[jIdx].truck_day_rate;
        delete currentJob.materials[jIdx].day_rate;
        delete currentJob.materials[jIdx].total_tolls;
        delete currentJob.materials[jIdx].load_mins;
        delete currentJob.materials[jIdx].tip_mins;
        delete currentJob.materials[jIdx].start_time;
        delete currentJob.materials[jIdx].finish_time;
        currentJob = { ...currentJob };
      }
    }
  }

  let materialPersistTimeout = null;

  function handleMaterialChange(e) {
    const updatedMat = e.detail?.material || selectedMaterialForAllocation;
    if (!updatedMat) return;

    if (selectedQuote && Array.isArray(selectedQuote.quote_materials)) {
      const idx = selectedQuote.quote_materials.findIndex(m => 
        (m.id && updatedMat.id && String(m.id) === String(updatedMat.id)) || 
        (m.material_name && (m.material_name === updatedMat.material_name || m.material_name === updatedMat.name)) ||
        (m.name && (m.name === updatedMat.name || m.name === updatedMat.material_name))
      );
      if (idx !== -1) {
        selectedQuote.quote_materials[idx] = { 
          ...selectedQuote.quote_materials[idx], 
          ...updatedMat
        };
        selectedQuote = { ...selectedQuote };
      }
    }

    if (currentJob && Array.isArray(currentJob.materials)) {
      let jIdx = currentJob.materials.findIndex(m => 
        (m.id && updatedMat.id && String(m.id) === String(updatedMat.id)) || 
        (m.__job_material_number__ && updatedMat.__job_material_number__ && String(m.__job_material_number__) === String(updatedMat.__job_material_number__)) ||
        (m.name && (m.name === updatedMat.name || m.name === updatedMat.material_name))
      );

      if (jIdx === -1 && currentJob.materials.length === 1) {
        jIdx = 0;
      }

      const jobMatUpdates = {
        name: updatedMat.name || updatedMat.material_name,
        load_address: updatedMat.load_address || null,
        siteAddress: updatedMat.siteAddress || (typeof updatedMat.load_address === 'string' ? updatedMat.load_address : (updatedMat.load_address?.place_name || updatedMat.load_address?.street || "")),
        disposal_address: updatedMat.disposal_address || null,
        dispose_address: updatedMat.dispose_address || (typeof updatedMat.disposal_address === 'string' ? updatedMat.disposal_address : (updatedMat.disposal_address?.place_name || updatedMat.disposal_address?.street || "")),
        yard: updatedMat.yard || null,
        yard_id: updatedMat.yard_id ?? (typeof updatedMat.yard === 'object' ? updatedMat.yard?.id : (typeof updatedMat.yard === 'number' ? updatedMat.yard : null)) ?? null,
        yard_name: updatedMat.yard_name ?? (typeof updatedMat.yard === 'object' ? updatedMat.yard?.name : null) ?? null,
        suburb: updatedMat.suburb ?? (typeof updatedMat.yard === 'object' ? (updatedMat.yard?.suburb || updatedMat.yard?.address?.suburb) : "") ?? "",
        image: updatedMat.image ?? (typeof updatedMat.yard === 'object' ? updatedMat.yard?.image : null) ?? null,
        contact_name: updatedMat.contact_name ?? "",
        contact_phone: updatedMat.contact_phone ?? "",
        contact_email: updatedMat.contact_email ?? "",
        contact_position: updatedMat.contact_position ?? "",
        main_contact: updatedMat.main_contact || null
      };

      if (jIdx !== -1) {
        currentJob.materials[jIdx] = { 
          ...currentJob.materials[jIdx], 
          ...jobMatUpdates
        };
      } else {
        currentJob.materials.push({
          id: updatedMat.id || currentJob.materials.length + 1,
          ...jobMatUpdates
        });
      }
      currentJob = { ...currentJob };

      if (materialPersistTimeout) clearTimeout(materialPersistTimeout);
      materialPersistTimeout = setTimeout(async () => {
        try {
          if (currentJob) {
            const saved = await updateJob(currentJob);
            if (saved) {
              currentJob = { ...currentJob, ...saved };
            }
          }
        } catch (err) {
          console.warn("[handleMaterialChange] Auto-save job material update error:", err);
        }
      }, 400);
    }
  }

  async function persistAllocationsToBackend(allocsToPersist) {
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
      autoSaveTimeout = null;
    }

    const allocs = allocsToPersist || currentAllocations;
    let targetJob = currentJob;
    if (!targetJob && isConverted) {
      try {
        const jobsData = await fetchJobs();
        const allJobs = Array.isArray(jobsData) ? jobsData : (jobsData?.jobs || []);
        targetJob = allJobs.find(j => 
          (j.quote_id && String(j.quote_id) === String(selectedQuote?.id || selectedQuote?._id)) ||
          (j.quote_number && selectedQuote?.quote_number && String(j.quote_number) === String(selectedQuote.quote_number))
        );
      } catch (_) {}
    }

    if (targetJob) {
      const updatedMaterials = (targetJob.materials || []).map(jobMat => {
        let allocEntry = Object.entries(allocs || {}).find(([k, a]) => {
          if (!a) return false;
          const aName = (a.materialName || k || "").trim().toLowerCase();
          const jName = (jobMat.name || "").trim().toLowerCase();
          return aName === jName || k === String(jobMat.id) || k === String(jobMat.__job_material_number__);
        })?.[1];

        if (!allocEntry && (targetJob.materials || []).length === 1 && allocs && Object.keys(allocs).length === 1) {
          allocEntry = Object.values(allocs)[0];
        }

        const matchedBoardMat = (selectedQuote?.quote_materials || []).find(m =>
          (m.id && jobMat.id && String(m.id) === String(jobMat.id)) ||
          (m.name && jobMat.name && m.name === jobMat.name)
        );

        const baseJobStartDate = targetJob.start_date || startDate;
        const assignedTrucks = mapAllocationsToAssignedTrucks(allocEntry, jobMat, baseJobStartDate);
        return {
          ...jobMat,
          load_address: matchedBoardMat?.load_address !== undefined ? matchedBoardMat.load_address : (jobMat.load_address || null),
          siteAddress: matchedBoardMat?.siteAddress !== undefined ? matchedBoardMat.siteAddress : (jobMat.siteAddress || ""),
          disposal_address: matchedBoardMat?.disposal_address !== undefined ? matchedBoardMat.disposal_address : (jobMat.disposal_address || null),
          dispose_address: matchedBoardMat?.dispose_address !== undefined ? matchedBoardMat.dispose_address : (jobMat.dispose_address || ""),
          yard: matchedBoardMat?.yard !== undefined ? matchedBoardMat.yard : (jobMat.yard || null),
          yard_id: matchedBoardMat?.yard_id !== undefined ? matchedBoardMat.yard_id : (jobMat.yard_id ?? null),
          yard_name: matchedBoardMat?.yard_name !== undefined ? matchedBoardMat.yard_name : (jobMat.yard_name ?? null),
          suburb: matchedBoardMat?.suburb !== undefined ? matchedBoardMat.suburb : (jobMat.suburb ?? ""),
          image: matchedBoardMat?.image !== undefined ? matchedBoardMat.image : (jobMat.image ?? null),
          contact_name: matchedBoardMat?.contact_name !== undefined ? matchedBoardMat.contact_name : (jobMat.contact_name ?? ""),
          contact_phone: matchedBoardMat?.contact_phone !== undefined ? matchedBoardMat.contact_phone : (jobMat.contact_phone ?? ""),
          contact_email: matchedBoardMat?.contact_email !== undefined ? matchedBoardMat.contact_email : (jobMat.contact_email ?? ""),
          contact_position: matchedBoardMat?.contact_position !== undefined ? matchedBoardMat.contact_position : (jobMat.contact_position ?? ""),
          main_contact: matchedBoardMat?.main_contact !== undefined ? matchedBoardMat.main_contact : (jobMat.main_contact || null),
          allocated_trucks: allocEntry ? assignedTrucks : (jobMat.allocated_trucks || [])
        };
      });

      const updatedJob = {
        ...targetJob,
        materials: updatedMaterials
      };

      const savedJob = await updateJob(updatedJob);
      currentJob = savedJob || updatedJob;

      // Keep selectedQuote materials in sync
      if (selectedQuote && Array.isArray(selectedQuote.quote_materials)) {
        selectedQuote.quote_materials.forEach(qm => {
          const matchedMat = (currentJob.materials || []).find(jm => jm.name === qm.material_name || (jm.id && qm.id && String(jm.id) === String(qm.id)));
          if (matchedMat) {
            qm.allocated_trucks = matchedMat.allocated_trucks || [];
            qm.load_address = matchedMat.load_address || null;
            qm.siteAddress = matchedMat.siteAddress || "";
            qm.disposal_address = matchedMat.disposal_address || null;
            qm.dispose_address = matchedMat.dispose_address || "";
            qm.yard = matchedMat.yard || null;
            qm.yard_id = matchedMat.yard_id ?? null;
            qm.yard_name = matchedMat.yard_name ?? null;
            qm.suburb = matchedMat.suburb ?? "";
            qm.image = matchedMat.image ?? null;
            qm.contact_name = matchedMat.contact_name ?? "";
            qm.contact_phone = matchedMat.contact_phone ?? "";
            qm.contact_email = matchedMat.contact_email ?? "";
            qm.contact_position = matchedMat.contact_position ?? "";
            qm.main_contact = matchedMat.main_contact || null;
          }
        });
        selectedQuote = { ...selectedQuote };
      }

      return savedJob;
    } else if (selectedQuote) {
      // Unconverted quote: persist allocated_trucks onto quote_materials in MongoDB
      const baseQuoteDate = selectedQuote.quote_date || startDate;
      const updatedQuoteMats = (selectedQuote.quote_materials || []).map(qm => {
        const key = qm.id || qm.__job_material_number__ || qm.material_name || "mat_default";
        const allocEntry = allocs[key] || allocs[qm.material_name];
        if (allocEntry && allocEntry.days) {
          return {
            ...qm,
            allocated_trucks: mapAllocationsToAssignedTrucks(allocEntry, qm, baseQuoteDate)
          };
        }
        return qm;
      });

      selectedQuote.quote_materials = updatedQuoteMats;
      selectedQuote = { ...selectedQuote };

      try {
        await updateQuoteDoc(selectedQuote);
      } catch (err) {
        console.warn("Could not persist quote materials allocation:", err);
      }
      return selectedQuote;
    }
  }

  function handleAllocationsChanged(event) {
    currentAllocations = event.detail.allocations || {};
    if (currentJob) {
      if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
      autoSaveTimeout = setTimeout(async () => {
        try {
          await persistAllocationsToBackend(currentAllocations);
        } catch (e) {
          console.warn("Background auto-save failed:", e);
        }
      }, 1500);
    }
  }

  let isGenerating = false;
  let generateError = "";
  let generationSuccess = null; // { message, job, quote }

  // Editable job generation parameters
  let jobStatus = "active";
  let startDate = new Date().toISOString().split("T")[0];
  let loadingUhf = "";
  let disposalUhf = "";
  let siteNotes = "";
  let poNumber = "";

  // START JOB modal state & functions
  let isStartJobModalOpen = false;
  let isStartingJob = false;
  let startJobSuccessNotice = null;
  let startJobError = "";
  let cachedCompanies = [];
  let cachedYards = [];

  // Delete Job modal state & functions
  let isDeleteJobModalOpen = false;
  let isDeletingJob = false;
  let deleteJobError = "";
  let deleteJobSuccessNotice = "";

  async function handleConfirmDeleteJob() {
    const jobToDelete = currentJob || matchingConvertedJob;
    if (!jobToDelete) {
      // Discard current unsaved draft configuration
      currentAllocations = {};
      selectedMaterialForAllocation = null;
      selectedQuote = null;
      $selectedQuoteForJob = null;
      isDeleteJobModalOpen = false;
      deleteJobSuccessNotice = "Job configuration was discarded.";
      setTimeout(() => {
        deleteJobSuccessNotice = "";
      }, 5000);
      return;
    }
    const jobId = jobToDelete.id || jobToDelete.__job_number__;
    if (!jobId) {
      deleteJobError = "Invalid job ID.";
      return;
    }

    isDeletingJob = true;
    deleteJobError = "";
    try {
      await deleteJob(jobId);

      const qId = jobToDelete.quote_id || selectedQuote?.id || selectedQuote?._id;
      if (qId) {
        try {
          await updateQuoteDoc(qId, { status: "draft" });
          const qIdx = quotes.findIndex((q) => String(q.id || q._id) === String(qId));
          if (qIdx !== -1) {
            quotes[qIdx] = { ...quotes[qIdx], status: "draft" };
            quotes = [...quotes];
          }
          if (selectedQuote && String(selectedQuote.id || selectedQuote._id) === String(qId)) {
            selectedQuote = { ...selectedQuote, status: "draft" };
          }
        } catch (e) {
          console.warn("Could not revert quote status to draft:", e);
        }
      }

      deleteJobSuccessNotice = `Job #${jobId} was permanently deleted.`;
      isDeleteJobModalOpen = false;
      currentJob = null;

      await loadJobs();
      await loadQuotes();

      if ($selectedJobForEdit && ($selectedJobForEdit.id === jobId || $selectedJobForEdit.__job_number__ === jobId)) {
        $selectedJobForEdit = null;
      }

      setTimeout(() => {
        deleteJobSuccessNotice = "";
      }, 5000);
    } catch (err) {
      console.error("Failed to delete job:", err);
      deleteJobError = err.message || "Failed to delete job.";
    } finally {
      isDeletingJob = false;
    }
  }

  function openStartJobModal() {
    startJobError = "";
    isStartJobModalOpen = true;
  }

  async function handleStartJobConfirmed(event) {
    const { startDate: chosenStartDate, mainContacts, secondaryContacts, drivers } = event.detail;
    isStartingJob = true;
    startJobError = "";
    try {
      if (currentJob) {
        const updatedJob = {
          ...currentJob,
          status: "active",
          start_date: chosenStartDate,
          materials: (currentJob.materials || []).map((m) => ({
            ...m,
            status: "active",
            start_date: chosenStartDate
          }))
        };
        const saved = await updateJob(updatedJob);
        currentJob = saved || updatedJob;
        await loadJobs();
      } else {
        startDate = chosenStartDate;
        jobStatus = "active";
        await handleCreateJob();
      }

      // Sync and activate job in live database via POST /jobs/live
      const targetJobId = currentJob?.id || currentJob?._id || currentJob?.__job_number__;
      if (targetJobId) {
        try {
          console.log(`[CreateJob] Activating job #${targetJobId} to live database via POST /jobs/live...`);
          await activateJobLive(targetJobId);
          console.log(`[CreateJob] Job #${targetJobId} successfully upserted to live database.`);
        } catch (liveErr) {
          console.error(`[CreateJob] Error activating job #${targetJobId} to live database:`, liveErr);
        }
      }

      // Notification Dispatch (Placeholder Logic)
      const emailRecipients = [...mainContacts, ...secondaryContacts].filter((c) => c.sendEmail);
      const smsRecipients = [...mainContacts, ...secondaryContacts].filter((c) => c.sendPhone);
      const driverRecipients = drivers.filter((d) => d.selected);

      console.log("[START JOB DISPATCH PLAN - PLACEHOLDER]", {
        jobId: currentJob?.__job_number__ || currentJob?.id,
        confirmedStartDate: chosenStartDate,
        summary: {
          emailsQueued: emailRecipients.length,
          smsQueued: smsRecipients.length,
          driversQueued: driverRecipients.length
        },
        emailRecipients,
        smsRecipients,
        driverRecipients
      });

      startJobSuccessNotice = {
        jobId: currentJob?.__job_number__ || currentJob?.id || "NEW",
        startDate: chosenStartDate,
        emailsCount: emailRecipients.length,
        smsCount: smsRecipients.length,
        driversCount: driverRecipients.length
      };

      isStartJobModalOpen = false;
    } catch (err) {
      console.error("Failed to start job:", err);
      startJobError = err.message || "Failed to start job.";
    } finally {
      isStartingJob = false;
    }
  }

  let isUpdatingStatus = false;

  async function handleStatusChange(event) {
    const newStatus = event.target.value;
    jobStatus = newStatus;

    if (currentJob) {
      isUpdatingStatus = true;
      generateError = "";
      try {
        const jId = currentJob.id || currentJob._id || currentJob.__job_number__;
        console.log(`[CreateJob] Updating status for job #${jId} to "${newStatus}"...`);

        const updatedJob = {
          ...currentJob,
          status: newStatus,
          materials: (currentJob.materials || []).map((m) => ({
            ...m,
            status: newStatus
          }))
        };

        const saved = await updateJob(updatedJob);
        currentJob = saved || updatedJob;
        jobStatus = currentJob.status || newStatus;

        // If changed to active, sync to live database
        if (newStatus.toLowerCase() === "active" && jId) {
          try {
            await activateJobLive(jId);
            console.log(`[CreateJob] Job #${jId} successfully synced to live database.`);
          } catch (liveErr) {
            console.warn(`[CreateJob] Failed to sync active job #${jId} to live DB:`, liveErr);
          }
        }

        await loadJobs();
      } catch (err) {
        console.error("[CreateJob] Failed to update job status:", err);
        generateError = err.message || "Failed to update job status.";
      } finally {
        isUpdatingStatus = false;
      }
    } else {
      jobStatus = newStatus;
    }
  }

  onMount(async () => {
    await Promise.all([loadQuotes(), loadJobs()]);
    // If a job was pre-selected from Quote Details or store, select it
    if ($selectedJobForEdit) {
      selectJobFromStore($selectedJobForEdit);
    } else if ($selectedQuoteForJob) {
      selectQuoteFromStore($selectedQuoteForJob);
    }
    try {
      fleetTrucks = await fetchTrucks();
    } catch (e) {
      console.error("Failed to load fleet trucks in CreateJob:", e);
    }
  });

  // Keep synced if $selectedJobForEdit updates while this tab is open
  $: if ($selectedJobForEdit) {
    const jobTarget = $selectedJobForEdit;
    $selectedJobForEdit = null;
    if (jobsList.length > 0) {
      selectJobFromStore(jobTarget);
    } else {
      loadJobs().then(() => selectJobFromStore(jobTarget));
    }
  }

  // Keep synced if $selectedQuoteForJob updates while this tab is open
  $: if ($selectedQuoteForJob && (!selectedQuote || selectedQuote.id !== $selectedQuoteForJob.id)) {
    selectQuoteFromStore($selectedQuoteForJob);
  }

  function selectJobFromStore(jobTarget) {
    if (!jobTarget) return;
    const jId = String(jobTarget.id || jobTarget._id || jobTarget.__job_number__ || "");
    const qId = String(jobTarget.quote_id || "");
    const qNum = String(jobTarget.quote_number || "");

    const matched = jobsList.find(j =>
      (jId && (String(j.id) === jId || String(j._id) === jId || String(j.__job_number__) === jId)) ||
      (qId && String(j.quote_id) === qId) ||
      (qNum && String(j.quote_number) === qNum)
    ) || jobTarget;

    isSidebarOpen = true;
    selectJob(matched);
  }

  function selectQuoteFromStore(target) {
    if (!target) return;
    const found = quotes.find((q) => q.id === target.id || q._id === target.id);
    const item = found || target;
    const targetId = String(item.id || item._id || "");
    const targetQNum = String(item.quote_number || "");

    const matchingJob = jobsList.find(j => 
      (targetId && j.quote_id && String(j.quote_id) === targetId) ||
      (targetQNum && j.quote_number && String(j.quote_number) === targetQNum)
    );
    if (matchingJob) {
      selectJobFromStore(matchingJob);
      return;
    }

    if ((item.status || "").toLowerCase() === "converted") {
      loadJobs().then(() => {
        const freshJob = jobsList.find(j => 
          (targetId && j.quote_id && String(j.quote_id) === targetId) ||
          (targetQNum && j.quote_number && String(j.quote_number) === targetQNum)
        );
        if (freshJob) {
          selectJobFromStore(freshJob);
        } else {
          selectQuote(item);
        }
      });
      return;
    }
    selectQuote(item);
  }

  async function loadQuotes() {
    isLoadingQuotes = true;
    quotesError = "";
    try {
      const data = await fetchQuotes();
      const list = Array.isArray(data) ? data : data?.quotes || [];
      // Sort newest quotes first (by id descending or quote_date)
      quotes = list.sort((a, b) => {
        const idA = Number(a.id || a._id || 0);
        const idB = Number(b.id || b._id || 0);
        return idB - idA;
      });
      // If we had a selected quote, refresh its reference
      if (selectedQuote && !currentJob) {
        const refreshed = quotes.find(
          (q) => q.id === selectedQuote.id || q._id === selectedQuote.id
        );
        if (refreshed) selectedQuote = refreshed;
      }
    } catch (err) {
      console.error("Failed to load quotes:", err);
      quotesError = err.message || "Failed to load quotes from database";
    } finally {
      isLoadingQuotes = false;
    }
  }

  async function loadJobs() {
    isLoadingJobs = true;
    jobsError = "";
    try {
      const data = await fetchJobs(['active', 'proposed', 'pending', 'completed', 'cancelled']);
      const list = Array.isArray(data) ? data : data?.jobs || [];
      jobsList = list.sort((a, b) => {
        const idA = Number(a.id || a._id || 0);
        const idB = Number(b.id || b._id || 0);
        return idB - idA;
      });
      // If we had a selected job, refresh its reference
      if (currentJob) {
        const refreshed = jobsList.find(
          (j) => j.id === currentJob.id || j.__job_number__ === currentJob.__job_number__
        );
        if (refreshed) {
          currentJob = refreshed;
          if (selectedQuote && Array.isArray(selectedQuote.quote_materials) && Array.isArray(refreshed.materials)) {
            selectedQuote.quote_materials = selectedQuote.quote_materials.map((qm, idx) => {
              const matchedMat = (refreshed.materials || []).find(jm => 
                (jm.id && qm.id && String(jm.id) === String(qm.id)) ||
                (jm.__job_material_number__ && qm.__job_material_number__ && String(jm.__job_material_number__) === String(qm.__job_material_number__)) ||
                (jm.name && (jm.name === qm.material_name || jm.name === qm.name))
              ) || refreshed.materials[idx];
              if (matchedMat) {
                return {
                  ...qm,
                  disposal_address: matchedMat.disposal_address || null,
                  dispose_address: matchedMat.dispose_address || "",
                  load_address: matchedMat.load_address || null,
                  siteAddress: matchedMat.siteAddress || "",
                  yard: matchedMat.yard || null,
                  yard_id: matchedMat.yard_id ?? null,
                  yard_name: matchedMat.yard_name ?? null,
                  suburb: matchedMat.suburb ?? "",
                  image: matchedMat.image ?? null,
                  contact_name: matchedMat.contact_name ?? "",
                  contact_phone: matchedMat.contact_phone ?? "",
                  contact_email: matchedMat.contact_email ?? "",
                  contact_position: matchedMat.contact_position ?? "",
                  main_contact: matchedMat.main_contact || null
                };
              }
              return qm;
            });
            selectedQuote = { ...selectedQuote };
          }
        }
      }
    } catch (err) {
      console.error("Failed to load jobs in CreateJob:", err);
      jobsError = err.message || "Failed to load jobs from database";
    } finally {
      isLoadingJobs = false;
    }
  }

  async function selectQuote(quote) {
    selectedQuote = quote;
    $selectedQuoteForJob = quote;
    currentJob = null;
    generateError = "";
    generationSuccess = null;
    siteNotes = quote.notes || "";
    poNumber = "";
    loadingUhf = "";
    disposalUhf = "";
    jobStatus = "active";
    if (quote.quote_date) {
      try {
        startDate = quote.quote_date.split("T")[0];
      } catch (_) {
        startDate = new Date().toISOString().split("T")[0];
      }
    } else {
      startDate = new Date().toISOString().split("T")[0];
    }
    selectedMaterialForAllocation = (quote && quote.quote_materials && quote.quote_materials.length > 0)
      ? quote.quote_materials[0]
      : null;
    currentAllocations = {};
  }

  async function selectJob(job) {
    currentJob = job;
    generateError = "";
    generationSuccess = null;
    siteNotes = job.site_notes || job.notes || "";
    jobStatus = job.status || "proposed";
    poNumber = job.po_number || "";
    loadingUhf = job.load_uhf_channel || "";
    disposalUhf = job.dispose_uhf_channel || "";

    if (job.start_date) {
      try {
        startDate = job.start_date.split("T")[0];
      } catch (_) {
        startDate = new Date().toISOString().split("T")[0];
      }
    } else {
      startDate = new Date().toISOString().split("T")[0];
    }

    // Find linked quote if available
    const qId = job.quote_id;
    const qNum = job.quote_number;
    let matchingQuote = null;
    if (qId || qNum) {
      matchingQuote = quotes.find(q =>
        (qId && String(q.id || q._id) === String(qId)) ||
        (qNum && q.quote_number && String(q.quote_number) === String(qNum))
      );
    }

    // Build canonical Job materials strictly from job.materials (with fallbacks to job-level fields)
    const rawJobMats = (Array.isArray(job.materials) && job.materials.length > 0)
      ? job.materials
      : (Array.isArray(matchingQuote?.quote_materials) && matchingQuote.quote_materials.length > 0
          ? matchingQuote.quote_materials.map((qm, idx) => ({
              id: qm.id || idx + 1,
              name: qm.material_name || `Material ${idx + 1}`,
              amount: qm.total_tonnes || qm.quantity || 0,
              load_address: job.load_address || (job.siteAddress ? { street: job.siteAddress, place_name: job.siteAddress } : null) || qm.load_address || null,
              siteAddress: job.siteAddress || (typeof job.load_address === 'string' ? job.load_address : (job.load_address?.place_name || job.load_address?.street || '')) || qm.siteAddress || "",
              disposal_address: job.disposal_address || (job.dispose_address ? { street: job.dispose_address, place_name: job.dispose_address } : null) || qm.disposal_address || null,
              dispose_address: job.dispose_address || (typeof job.disposal_address === 'string' ? job.disposal_address : (job.disposal_address?.place_name || job.disposal_address?.street || '')) || qm.dispose_address || "",
              yard: job.yard || qm.yard || null,
              yard_id: job.yard_id ?? (typeof job.yard === 'object' ? job.yard?.id : job.yard) ?? qm.yard_id ?? null,
              yard_name: job.yard_name ?? (typeof job.yard === 'object' ? job.yard?.name : null) ?? qm.yard_name ?? null,
              suburb: job.suburb ?? (typeof job.yard === 'object' ? (job.yard?.suburb || job.yard?.address?.suburb) : null) ?? qm.suburb ?? "",
              image: job.image ?? (typeof job.yard === 'object' ? job.yard?.image : null) ?? qm.image ?? null,
              allocated_trucks: qm.allocated_trucks || [],
              operating_hours: qm.operating_hours || { start_time: "07:00", finish_time: "17:00", load_mins: 15, tip_mins: 15 },
              fleet: qm.fleet || { truck_type: "10 wheeler - 19t", truck_capacity_tonne: 19, trailer_type: "8 wheeler - 20t", trailer_capacity_tonne: 19, total_capacity_tonne: 38, truck_day_rate: 1400 },
              schedule: qm.schedule || { runs_per_day: 5, total_loads: 1, allocated_trucks: 1, estimated_days: 1 }
            }))
          : [{
              id: 1,
              name: "Bulk Material",
              amount: 0,
              load_address: job.load_address || (job.siteAddress ? { street: job.siteAddress, place_name: job.siteAddress } : null),
              siteAddress: job.siteAddress || "",
              disposal_address: job.disposal_address || (job.dispose_address ? { street: job.dispose_address, place_name: job.dispose_address } : null),
              dispose_address: job.dispose_address || "",
              yard: job.yard || null
            }]);

    const jobMats = rawJobMats.map((jm, idx) => {
      // Find matching quote material strictly for quote pricing/reference, NEVER for addresses or yard!
      const qm = matchingQuote?.quote_materials?.find(q =>
        (q.material_name && jm.name && q.material_name.trim().toLowerCase() === jm.name.trim().toLowerCase()) ||
        (q.id && jm.id && String(q.id) === String(jm.id))
      );

      // RESOLVE STRICTLY FROM JOB DATA (Job Material first, then Job-level fallback)
      const loadAddr = jm.load_address || (job.load_address ? job.load_address : (job.siteAddress ? { street: job.siteAddress, place_name: job.siteAddress } : null));
      const loadAddrStr = (typeof jm.load_address === 'string' ? jm.load_address : (jm.load_address?.place_name || jm.load_address?.street || ''))
        || jm.siteAddress
        || jm.loadAddress
        || (typeof job.load_address === 'string' ? job.load_address : (job.load_address?.place_name || job.load_address?.street || ''))
        || job.siteAddress
        || '';

      const dispAddr = jm.disposal_address || (job.disposal_address ? job.disposal_address : (job.dispose_address ? { street: job.dispose_address, place_name: job.dispose_address } : null));
      const dispAddrStr = (typeof jm.disposal_address === 'string' ? jm.disposal_address : (jm.disposal_address?.place_name || jm.disposal_address?.street || ''))
        || jm.dispose_address
        || (typeof job.disposal_address === 'string' ? job.disposal_address : (job.disposal_address?.place_name || job.disposal_address?.street || ''))
        || job.dispose_address
        || '';

      const yardObj = jm.yard || job.yard || null;
      const yardId = jm.yard_id ?? (typeof yardObj === 'object' ? yardObj?.id : yardObj) ?? job.yard_id ?? null;
      const yardName = jm.yard_name ?? (typeof yardObj === 'object' ? yardObj?.name : (typeof yardObj === 'string' ? yardObj : null)) ?? job.yard_name ?? null;
      const yardSuburb = jm.suburb ?? (typeof yardObj === 'object' ? (yardObj?.suburb || yardObj?.address?.suburb) : null) ?? job.suburb ?? '';
      const yardImage = jm.image ?? (typeof yardObj === 'object' ? yardObj?.image : null) ?? job.image ?? null;

      const contactName = jm.contact_name ?? jm.main_contact?.name ?? (typeof yardObj === 'object' ? (yardObj?.contact_name || yardObj?.contact?.name) : null) ?? job.contact_name ?? '';
      const contactPhone = jm.contact_phone ?? jm.main_contact?.phone ?? (typeof yardObj === 'object' ? (yardObj?.phone || yardObj?.contact?.phone) : null) ?? job.contact_phone ?? '';
      const contactEmail = jm.contact_email ?? jm.main_contact?.email ?? (typeof yardObj === 'object' ? (yardObj?.email || yardObj?.contact?.email) : null) ?? job.contact_email ?? '';
      const contactPos = jm.contact_position ?? jm.main_contact?.position ?? (typeof yardObj === 'object' ? (yardObj?.position || yardObj?.contact?.position) : null) ?? job.contact_position ?? '';
      const mainContact = jm.main_contact || (typeof yardObj === 'object' ? yardObj?.main_contact : null) || (contactName ? { name: contactName, phone: contactPhone, email: contactEmail, position: contactPos } : null);

      return {
        ...(qm || {}),
        ...jm,
        id: jm.id || jm.__job_material_number__ || idx + 1,
        __job_material_number__: jm.__job_material_number__ || jm.id || idx + 1,
        material_name: jm.name || jm.material_name || qm?.material_name || `Material ${idx + 1}`,
        name: jm.name || jm.material_name || qm?.material_name || `Material ${idx + 1}`,
        quantity: jm.amount || jm.quantity || qm?.quantity || 0,
        total_tonnes: jm.amount || jm.total_tonnes || qm?.total_tonnes || 0,
        cost_per_tonne: jm.cost_per_tonne ?? qm?.cost_per_tonne ?? null,
        proposed_unit_price: jm.proposed_unit_price ?? jm.unit_price ?? qm?.proposed_unit_price ?? qm?.unit_price ?? null,
        unit_price: jm.unit_price ?? jm.proposed_unit_price ?? qm?.unit_price ?? qm?.proposed_unit_price ?? null,
        proposed_total_price: jm.proposed_total_price ?? jm.total_price ?? qm?.proposed_total_price ?? qm?.total_price ?? null,
        total_price: jm.total_price ?? jm.proposed_total_price ?? qm?.total_price ?? qm?.proposed_total_price ?? null,
        load_address: loadAddr,
        siteAddress: loadAddrStr,
        loadAddress: loadAddrStr,
        disposal_address: dispAddr,
        dispose_address: dispAddrStr,
        yard: yardObj,
        yard_id: yardId,
        yard_name: yardName,
        suburb: yardSuburb,
        image: yardImage,
        contact_name: contactName,
        contact_phone: contactPhone,
        contact_email: contactEmail,
        contact_position: contactPos,
        main_contact: mainContact,
        allocated_trucks: Array.isArray(jm.allocated_trucks) ? [...jm.allocated_trucks] : (qm?.allocated_trucks ? [...qm.allocated_trucks] : []),
        load_uhf_channel: jm.load_uhf_channel || job.load_uhf_channel || qm?.load_uhf_channel || "",
        dispose_uhf_channel: jm.dispose_uhf_channel || job.dispose_uhf_channel || qm?.dispose_uhf_channel || "",
        operating_hours: jm.operating_hours || qm?.operating_hours || { start_time: "07:00", finish_time: "17:00", load_mins: 15, tip_mins: 15 },
        fleet: jm.fleet || qm?.fleet || { truck_type: "10 wheeler - 19t", truck_capacity_tonne: 19, trailer_type: "8 wheeler - 20t", trailer_capacity_tonne: 19, total_capacity_tonne: 38, truck_day_rate: 1400 },
        schedule: {
          runs_per_day: Number(jm.schedule?.runs_per_day ?? qm?.schedule?.runs_per_day ?? 5),
          total_loads: Number(jm.schedule?.total_loads ?? qm?.schedule?.total_loads ?? 1),
          allocated_trucks: parseInt(jm.schedule?.allocated_trucks ?? qm?.schedule?.allocated_trucks, 10) || (Array.isArray(jm.allocated_trucks) && jm.allocated_trucks.length > 0 ? jm.allocated_trucks.length : 1),
          estimated_days: Number(jm.schedule?.estimated_days ?? qm?.schedule?.estimated_days ?? 1),
          days_single_truck: Number(jm.schedule?.days_single_truck ?? qm?.schedule?.days_single_truck ?? 1),
          full_days: Number(jm.schedule?.full_days ?? qm?.schedule?.full_days ?? 1),
          remainder_days: Number(jm.schedule?.remainder_days ?? qm?.schedule?.remainder_days ?? 0),
          total_tolls: Number(jm.schedule?.total_tolls ?? qm?.schedule?.total_tolls ?? 0)
        }
      };
    });

    const client = (job.customer && typeof job.customer === 'object') ? job.customer : {};
    const compName = client.name
      || (typeof job.customer === 'string' ? job.customer : "")
      || matchingQuote?.customer_summary?.name
      || (typeof matchingQuote?.customer === 'object' ? matchingQuote.customer?.name : matchingQuote?.customer)
      || "Unnamed Customer";

    selectedQuote = {
      ...(matchingQuote || {}),
      id: matchingQuote?.id || job.id,
      quote_number: job.quote_number || matchingQuote?.quote_number || `JB-${job.__job_number__ || job.id}`,
      status: job.status || "converted",
      isDirectJob: !matchingQuote,
      customer: {
        ...(matchingQuote?.customer && typeof matchingQuote.customer === 'object' ? matchingQuote.customer : {}),
        ...client,
        name: compName
      },
      customer_summary: matchingQuote?.customer_summary || { name: compName },
      quote_materials: jobMats,
      financial_summary: {
        total_tonnes: jobMats.reduce((acc, m) => acc + Number(m.quantity || m.total_tonnes || 0), 0)
      }
    };

    currentJob.materials = jobMats;
    $selectedQuoteForJob = selectedQuote;
    selectedMaterialForAllocation = (jobMats.length > 0) ? jobMats[0] : null;
    currentAllocations = {};
  }

  $: quotesCount = quotes.filter((q) => (q.status || "draft").toLowerCase() !== "converted").length;
  $: jobsCount = jobsList.length;
  $: isConverted = !!(selectedQuote && (selectedQuote.status || "").toLowerCase() === "converted");

  $: matchingConvertedJob = jobsList.find((j) =>
    (j.quote_id && selectedQuote && String(j.quote_id) === String(selectedQuote.id || selectedQuote._id)) ||
    (j.quote_number && selectedQuote?.quote_number && String(j.quote_number) === String(selectedQuote.quote_number))
  ) || null;

  $: isJobActive = Boolean(
    (currentJob && ((currentJob.status || "").toLowerCase() === "active" || (jobStatus || "").toLowerCase() === "active")) ||
    (matchingConvertedJob && (matchingConvertedJob.status || "").toLowerCase() === "active")
  );

  async function handleCreateJob() {
    if (!selectedQuote) return;
    const qId = selectedQuote.id || selectedQuote._id;
    if (!qId) {
      generateError = "Quote must have an assigned database ID to generate a job.";
      return;
    }

    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
      autoSaveTimeout = null;
    }

    isGenerating = true;
    generateError = "";
    generationSuccess = null;

    try {
      let targetJob = currentJob;
      if (!targetJob && isConverted) {
        try {
          const jobsData = await fetchJobs();
          const allJobs = Array.isArray(jobsData) ? jobsData : (jobsData?.jobs || []);
          targetJob = allJobs.find(j => 
            (j.quote_id && String(j.quote_id) === String(qId)) ||
            (j.quote_number && selectedQuote.quote_number && String(j.quote_number) === String(selectedQuote.quote_number))
          );
        } catch (_) {}
      }

      if (targetJob) {
        // Direct update of the existing job and its truck allocations
        const clientData = (targetJob.customer && typeof targetJob.customer === 'object') ? targetJob.customer : {};
        const clientPhone = clientData.phone || targetJob.contacts?.[0]?.phone || "";
        const clientObj = {
          name: clientData.name || (typeof targetJob.customer === 'string' ? targetJob.customer : ""),
          phone: clientPhone ? toE164(clientPhone) : "",
          address: clientData.address || targetJob.address || {}
        };
        const updatedJob = {
          ...targetJob,
          status: currentJob?.status || jobStatus || targetJob.status || "proposed",
          customer: clientObj,
          po_number: poNumber,
          start_date: startDate,
          load_interval: parseInt(targetJob.load_interval, 10) || 15,
          days_count: parseInt(targetJob.days_count, 10) || 1,
          load_uhf_channel: loadingUhf,
          dispose_uhf_channel: disposalUhf,
          site_notes: siteNotes,
          notes: siteNotes
        };
        delete updatedJob.company;
        delete updatedJob.company_id;
        delete updatedJob.start_time;
        delete updatedJob.finish_time;

        if (Array.isArray(updatedJob.materials)) {
          updatedJob.materials = buildJobMaterialsPayload(
            updatedJob.materials,
            currentAllocations,
            selectedQuote,
            { loadingUhf, disposalUhf, siteNotes, startDate }
          );
        }

        const savedJob = await updateJob(updatedJob);
        currentJob = savedJob || updatedJob;
        jobStatus = currentJob.status || updatedJob.status;
        generationSuccess = {
          message: "Job saved successfully",
          job: currentJob
        };
        await loadJobs();

        if ((currentJob.status || '').toLowerCase() === 'active') {
          const tId = currentJob.id || currentJob._id || currentJob.__job_number__;
          if (tId) {
            try {
              await activateJobLive(tId);
            } catch (liveErr) {
              console.warn(`[CreateJob] Live sync on save active job #${tId} failed:`, liveErr);
            }
          }
        }

        // Keep quote materials in memory in sync with these allocations and material details
        if (Array.isArray(selectedQuote.quote_materials)) {
          selectedQuote.quote_materials = selectedQuote.quote_materials.map((qm, idx) => {
            const matchedMat = (currentJob.materials || []).find(jm => 
              (jm.id && qm.id && String(jm.id) === String(qm.id)) ||
              (jm.__job_material_number__ && qm.__job_material_number__ && String(jm.__job_material_number__) === String(qm.__job_material_number__)) ||
              (jm.name && (jm.name === qm.material_name || jm.name === qm.name))
            ) || currentJob.materials?.[idx];
            if (matchedMat) {
              return {
                ...qm,
                allocated_trucks: matchedMat.allocated_trucks || [],
                disposal_address: matchedMat.disposal_address || null,
                dispose_address: matchedMat.dispose_address || "",
                load_address: matchedMat.load_address || null,
                siteAddress: matchedMat.siteAddress || "",
                yard: matchedMat.yard || null,
                yard_id: matchedMat.yard_id ?? null,
                yard_name: matchedMat.yard_name ?? null,
                suburb: matchedMat.suburb ?? "",
                image: matchedMat.image ?? null,
                contact_name: matchedMat.contact_name ?? "",
                contact_phone: matchedMat.contact_phone ?? "",
                contact_email: matchedMat.contact_email ?? "",
                contact_position: matchedMat.contact_position ?? "",
                main_contact: matchedMat.main_contact || null
              };
            }
            return qm;
          });
          selectedQuote = { ...selectedQuote };
        }
      } else {
        // Generating for the first time from Quote
        const dispatchParams = {
          po_number: poNumber,
          start_date: startDate,
          days_count: 1,
          start_time: "07:00",
          finish_time: "17:00",
          load_interval: 15,
          load_uhf_channel: loadingUhf,
          dispose_uhf_channel: disposalUhf,
          site_notes: siteNotes,
          notes: siteNotes,
          allocations: currentAllocations
        };
        const result = await generateJobFromQuote(qId, jobStatus, dispatchParams);
        generationSuccess = result;

        // Update quote status in current list
        selectedQuote.status = "converted";
        const idx = quotes.findIndex((q) => (q.id || q._id) === qId);
        if (idx !== -1) {
          quotes[idx].status = "converted";
          quotes = [...quotes];
        }
        $customerQuote.id = qId;
        $customerQuote.status = "converted";
        await Promise.all([loadQuotes(), loadJobs()]);

        if (result && result.job) {
          currentJob = result.job;
          if (Array.isArray(result.job.materials)) {
            const updatedJob = { ...result.job };
            updatedJob.materials = buildJobMaterialsPayload(
              updatedJob.materials,
              currentAllocations,
              selectedQuote,
              { loadingUhf, disposalUhf, siteNotes, startDate }
            );
            delete updatedJob.company;
            delete updatedJob.company_id;
            delete updatedJob.start_time;
            delete updatedJob.finish_time;

            const savedJob = await updateJob(updatedJob);
            currentJob = savedJob || updatedJob;
            generationSuccess.job = currentJob;
          }
        }
      }
    } catch (err) {
      console.error("Job save error:", err);
      generateError = err.message || "Failed to save job.";
    } finally {
      isGenerating = false;
    }
  }

  function goToActiveJobs() {
    const jobToView = currentJob || matchingConvertedJob;
    if (jobToView) {
      $selectedJobForEdit = jobToView;
      if (jobToView.id) {
        $selectedJobs = [jobToView.id];
      }
    }
    $activeTab = "jobs";
  }

  async function handleViewSourceQuote() {
    try {
      if (currentJob) {
        const qId = currentJob.quote_id || currentJob.quote_number;
        const matchingQuote = quotes.find((q) =>
          (currentJob.quote_id && String(q.id || q._id) === String(currentJob.quote_id)) ||
          (currentJob.quote_number && q.quote_number && String(q.quote_number) === String(currentJob.quote_number))
        );

        if (matchingQuote) {
          loadQuoteIntoCustomerQuoteStore(matchingQuote);
        } else if (qId) {
          await fetchAndLoadQuote(qId, selectedQuote || currentJob);
        } else if (selectedQuote) {
          loadQuoteIntoCustomerQuoteStore(selectedQuote);
        }
      } else if (selectedQuote) {
        loadQuoteIntoCustomerQuoteStore(selectedQuote);
      }
    } catch (err) {
      console.warn("Failed to load source quote into store:", err);
      if (selectedQuote) {
        loadQuoteIntoCustomerQuoteStore(selectedQuote);
      }
    } finally {
      $activeTab = "quote-details";
    }
  }

  async function goToQuotesAndRoutes() {
    if (currentJob || selectedQuote) {
      await handleViewSourceQuote();
    } else {
      $activeTab = "quote-details";
    }
  }

</script>

<div class="create-job-page">
  <!-- Top Navigation Header -->
  <header class="page-header">
    <div class="header-titles">
      <div class="header-badge-row">
        <span class="page-badge">Dispatch Management</span>
        <span class="step-badge">Step 2: Quote &rarr; Job</span>
      </div>
      <h1 class="page-title">Create Job from Quote</h1>
    </div>
  </header>

  <!-- Split Screen Layout -->
  <div class="studio-layout">
    <!-- Fleet Trucks Pullout Tab: At the top right, directly under <header> -->
    <button
      class="fleet-pullout-tab {isFleetDrawerOpen ? 'is-open' : 'is-closed'}"
      on:click={() => {
        if (!currentJob && !selectedQuote && jobsList.length > 0) {
          selectJob(jobsList[0]);
        }
        isFleetDrawerOpen = !isFleetDrawerOpen;
      }}
      title={isFleetDrawerOpen ? "Slide fleet trucks in (hide)" : `Available Fleet Trucks (${fleetTrucks.length || 29}) - Click to slide out`}
      type="button"
      aria-label="Toggle fleet trucks drawer"
    >
      <DeliveryTruck size={15} class="fleet-pullout-icon" />
      {#if isFleetDrawerOpen}
        <ChevronRight size={13} class="tab-chevron" />
      {:else}
        <ChevronLeft size={13} class="tab-chevron" />
      {/if}
    </button>

    <!-- Left Column: Jobs List (Collapsible Sliding Sidebar) -->
    <CreateJobSidebar
      {isSidebarOpen}
      {jobsList}
      {currentJob}
      {quotes}
      {selectedQuote}
      {isLoadingJobs}
      {jobsError}
      on:selectJob={(e) => selectJob(e.detail)}
      on:toggleSidebar={toggleSidebar}
      on:reloadJobs={loadJobs}
    />


    <!-- Edge Slider Handle (boundary tab between sidebar and workbench) -->
    <button
      class="sidebar-edge-tab {isSidebarOpen ? 'is-open' : 'is-closed'}"
      on:click={toggleSidebar}
      title={isSidebarOpen ? "Slide jobs list out (hide)" : "Slide jobs list in (show)"}
      type="button"
      aria-label="Toggle jobs sidebar"
    >
      <svg
        class="clipboard-tab-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        width="14"
        height="14"
        aria-hidden="true"
      >
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        <rect x="8" y="2" width="8" height="4" rx="1" ry="1" fill="currentColor" fill-opacity="0.25" />
        <path d="M9 11h6M9 15h6" />
      </svg>
      {#if isSidebarOpen}
        <ChevronLeft size={13} class="tab-chevron" />
      {:else}
        <ChevronRight size={13} class="tab-chevron" />
      {/if}
    </button>

    <!-- Right Column: Job Creation Workbench -->
    <main class="workbench-main">
      {#if creationMode === 'manual'}
        <!-- Manual Job Creation Workbench -->
        <div class="workbench-card manual-card">
          <div class="workbench-header">
            <div class="header-meta">
              <div class="header-tags">
                <span class="status-pill status-draft"><Add size={14} /> Direct Entry</span>
                <span class="quote-code-tag">Manual Job Creation</span>
              </div>
              <h2 class="workbench-client-title">Create Haulage Job Directly</h2>
              <span class="workbench-date">Define customer, delivery sites, materials, and fleet dispatch specifications without a pre-existing quote</span>
            </div>

            <div class="workbench-header-actions">
              <button class="btn btn-sm btn-outline" on:click={() => (creationMode = "quote")} type="button">
                <span>&larr; Switch to Quote Mode</span>
              </button>
              <button 
                class="btn btn-sm btn-primary" 
                type="button" 
                on:click={handleCreateManualJob}
                disabled={isCreatingManual}
              >
                {#if isCreatingManual}
                  <span class="btn-spinner"></span>
                  <span>Saving...</span>
                {:else}
                  <Save size={15} />
                  <span>Save Job</span>
                {/if}
              </button>
            </div>
          </div>

          {#if manualCreateSuccess}
            <div class="generation-success-banner">
              <div class="success-icon-wrap">
                <Checkmark size={24} />
              </div>
              <div class="success-details">
                <strong>Success! Job #{manualCreateSuccess.id || manualCreateSuccess.__job_number__} Created!</strong>
                <p>The job has been created and saved to the operations database.</p>
                <div class="success-actions">
                  <button class="btn btn-primary" on:click={goToActiveJobs} type="button">
                    <span>View in Active Jobs &rarr;</span>
                  </button>
                  <button class="btn btn-outline" on:click={() => (manualCreateSuccess = null)} type="button">
                    <span>Create Another</span>
                  </button>
                </div>
              </div>
            </div>
          {/if}

          {#if manualCreateError}
            <div class="error-banner mb-16">
              <WarningFilled size={18} />
              <span>{manualCreateError}</span>
            </div>
          {/if}

          <div class="manual-form-container">
            <!-- Client & Site -->
            <div class="manual-section">
              <h3 class="manual-sec-title">Client &amp; Site Details</h3>
              <div class="manual-grid">
                <div class="grid-col col-12">
                  <label class="field-label" for="manual-comp-name">Client / Company Name *</label>
                  <input id="manual-comp-name" type="text" class="form-input" bind:value={manualJob.companyName} placeholder="e.g. Apex Civil Group" required />
                </div>
                <div class="grid-col col-8">
                  <label class="field-label" for="manual-site-street">Site Address (Street)</label>
                  <input id="manual-site-street" type="text" class="form-input" bind:value={manualJob.street} placeholder="e.g. 142 Richmond Rd" />
                </div>
                <div class="grid-col col-4">
                  <label class="field-label" for="manual-site-suburb">Suburb</label>
                  <input id="manual-site-suburb" type="text" class="form-input" bind:value={manualJob.suburb} placeholder="e.g. Blacktown" />
                </div>
                <div class="grid-col col-4">
                  <label class="field-label" for="manual-site-state">State</label>
                  <input id="manual-site-state" type="text" class="form-input" bind:value={manualJob.state} placeholder="NSW" />
                </div>
                <div class="grid-col col-4">
                  <label class="field-label" for="manual-site-postcode">Postcode</label>
                  <input id="manual-site-postcode" type="text" class="form-input" bind:value={manualJob.postcode} placeholder="2148" />
                </div>
                <div class="grid-col col-4">
                  <label class="field-label" for="manual-job-status">Initial Status</label>
                  <select id="manual-job-status" class="form-input" bind:value={manualJob.status}>
                    <option value="active">Active (Fleet Operations)</option>
                    <option value="proposed">Proposed (Pending Approval)</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Site Contact & PO -->
            <div class="manual-section">
              <h3 class="manual-sec-title">Contact &amp; Order Reference</h3>
              <div class="manual-grid">
                <div class="grid-col col-6">
                  <label class="field-label" for="manual-site-contact">Site Contact Person</label>
                  <input id="manual-site-contact" type="text" class="form-input" bind:value={manualJob.contactName} placeholder="e.g. John Doe" />
                </div>
                <div class="grid-col col-6">
                  <label class="field-label" for="manual-site-phone">Contact Phone</label>
                  <PhoneInput id="manual-site-phone" bind:value={manualJob.contactPhone} placeholder="0412 345 678" />
                </div>
                <div class="grid-col col-6">
                  <label class="field-label" for="manual-job-po">Purchase Order #</label>
                  <input id="manual-job-po" type="text" class="form-input" bind:value={manualJob.poNumber} placeholder="e.g. PO-88210" />
                </div>
                <div class="grid-col col-6">
                  <label class="field-label" for="manual-job-start">Scheduled Start Date</label>
                  <input id="manual-job-start" type="date" class="form-input" bind:value={manualJob.startDate} />
                </div>
              </div>
            </div>

            <!-- Dispatch Timing & UHF -->
            <div class="manual-section">
              <h3 class="manual-sec-title">Dispatch Scheduling &amp; Radio Channels</h3>
              <div class="manual-grid">
                <div class="grid-col col-3">
                  <label class="field-label" for="manual-job-days">Duration (Days)</label>
                  <input id="manual-job-days" type="number" class="form-input" bind:value={manualJob.daysCount} min="1" max="30" />
                </div>
                <div class="grid-col col-3">
                  <label class="field-label" for="manual-job-time">Departure Time</label>
                  <input id="manual-job-time" type="time" class="form-input" bind:value={manualJob.startTime} />
                </div>
                <div class="grid-col col-3">
                  <label class="field-label" for="manual-job-finish">Finish Time</label>
                  <input id="manual-job-finish" type="time" class="form-input" bind:value={manualJob.finishTime} />
                </div>
                <div class="grid-col col-3">
                  <label class="field-label" for="manual-job-interval">Interval (mins)</label>
                  <input id="manual-job-interval" type="number" class="form-input" bind:value={manualJob.loadInterval} min="5" step="5" />
                </div>
                <div class="grid-col col-6">
                  <label class="field-label" for="manual-load-uhf">Site Loading UHF Channel</label>
                  <input id="manual-load-uhf" type="text" class="form-input" bind:value={manualJob.loadingUhf} placeholder="e.g. 29" />
                </div>
                <div class="grid-col col-6">
                  <label class="field-label" for="manual-dispose-uhf">Disposal Yard UHF</label>
                  <input id="manual-dispose-uhf" type="text" class="form-input" bind:value={manualJob.disposalUhf} placeholder="e.g. 14" />
                </div>
              </div>
            </div>

            <!-- Material & Notes -->
            <div class="manual-section">
              <h3 class="manual-sec-title">Material Package &amp; Notes</h3>
              <div class="manual-grid">
                <div class="grid-col col-8">
                  <label class="field-label" for="manual-mat-name">Material Name</label>
                  <input id="manual-mat-name" type="text" class="form-input" bind:value={manualJob.materialName} placeholder="e.g. VENM Clay & Sand" />
                </div>
                <div class="grid-col col-4">
                  <label class="field-label" for="manual-mat-amount">Target Tonnes</label>
                  <input id="manual-mat-amount" type="number" class="form-input" bind:value={manualJob.materialAmount} min="1" step="50" />
                </div>
                <div class="grid-col col-12">
                  <label class="field-label" for="manual-job-notes">Site Notes &amp; Dispatch Instructions</label>
                  <textarea id="manual-job-notes" class="form-textarea" rows="3" bind:value={manualJob.siteNotes} placeholder="Tipping dockets, access gate codes, PPE rules..."></textarea>
                </div>
              </div>
            </div>

            <!-- Submit Action -->
            <div class="manual-submit-row">
              <button 
                class="btn btn-primary btn-large" 
                type="button" 
                on:click={handleCreateManualJob}
                disabled={isCreatingManual}
              >
                {#if isCreatingManual}
                  <span class="btn-spinner"></span>
                  <span>Saving Job in Operations...</span>
                {:else}
                  <Save size={18} />
                  <span>Save Job in Operations</span>
                {/if}
              </button>
            </div>
          </div>
        </div>
      {:else if selectedQuote}
        <div class="workbench-card">
          <!-- Quote Header Card -->
          <div class="workbench-header">
            <div class="header-meta">
              <div class="header-tags">
                {#if currentJob}
                  <span class="quote-id-large">Job #{currentJob.__job_number__ || currentJob.id}</span>
                  {#if currentJob.quote_number || currentJob.quote_id}
                    <span class="quote-code-tag">From Quote {currentJob.quote_number || `QT-${currentJob.quote_id}`}</span>
                  {/if}
                  <span class="status-pill status-{(currentJob.status || 'proposed').toLowerCase()}">
                    <Checkmark size={14} /> {(currentJob.status || 'proposed').toUpperCase()}
                  </span>
                {:else}
                  <span class="quote-id-large">Quote #{selectedQuote.id || selectedQuote._id}</span>
                  <span class="quote-code-tag">{selectedQuote.quote_number || `QT-${selectedQuote.id}`}</span>
                  {#if isConverted}
                    <span class="status-pill status-converted"><Checkmark size={14} /> Converted to Job</span>
                  {:else}
                    <span class="status-pill status-draft">Ready to Convert</span>
                  {/if}
                {/if}
              </div>
              <h2 class="workbench-client-title">
                {currentJob ? getJobCustomerName(currentJob) : getQuoteCustomerName(selectedQuote)}
              </h2>
              <span class="workbench-date">
                {#if currentJob}
                  Job #{currentJob.__job_number__ || currentJob.id} | Scheduled: {currentJob.start_date ? currentJob.start_date.split('T')[0] : 'Today'} | Status: {(currentJob.status || 'proposed').toUpperCase()}
                {:else}
                  Issued: {selectedQuote.quote_date || "Today"} | Valid until: {selectedQuote.valid_until || "30 Days"}
                {/if}
              </span>
            </div>

            <div class="workbench-header-actions">
              <!-- STATUS DROPDOWN (to the left of Activate button) -->
              <div class="job-status-control" title="Change Job Status">
                <span class="status-indicator-dot dot-{(currentJob?.status || jobStatus || 'proposed').toLowerCase()}"></span>
                <select
                  class="job-header-status-select status-select-{(currentJob?.status || jobStatus || 'proposed').toLowerCase()}"
                  value={currentJob?.status || jobStatus || 'proposed'}
                  on:change={handleStatusChange}
                  title="Change job status"
                  aria-label="Change job status"
                  disabled={isUpdatingStatus}
                >
                  <option value="proposed">Proposed</option>
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                {#if isUpdatingStatus}
                  <span class="status-spinner-xs"></span>
                {/if}
              </div>

              <!-- START JOB / GO TO ACTIVE JOB BUTTON -->
              <button
                class="btn btn-sm btn-start-job {isJobActive ? 'btn-goto-active' : ''}"
                on:click={isJobActive ? goToActiveJobs : openStartJobModal}
                type="button"
                title={isJobActive ? "Go to this active job in Fleet Tracking" : "Start this job, confirm start date, and prepare stakeholder notifications"}
              >
                {#if isJobActive}
                  <ArrowRight size={15} />
                  <span>Go to ACTIVE job</span>
                {:else}
                  <PlayFilled size={15} />
                  <span>ACTIVATE JOB</span>
                {/if}
              </button>

              {#if currentJob?.quote_id || currentJob?.quote_number || (!currentJob && (selectedQuote?.id || selectedQuote?.quote_number))}
                <button class="btn btn-sm btn-outline" on:click={handleViewSourceQuote} type="button">
                  {currentJob ? "View Source Quote" : "Open in Quote Details"}
                </button>
              {/if}
              <button
                class="btn btn-sm btn-primary"
                on:click={handleCreateJob}
                disabled={isGenerating}
                type="button"
              >
                {#if isGenerating}
                  <span class="btn-spinner" />
                  <span>Saving...</span>
                {:else}
                  <Save size={15} />
                  <span>{currentJob || isConverted ? "Save Job" : "Save & Create Job"}</span>
                {/if}
              </button>
            </div>
          </div>

          <!-- Notification Banners -->
          {#if generateError}
            <div class="alert alert-danger">
              <WarningFilled size={18} />
              <div class="alert-content">
                <strong>Job Creation Failed:</strong>
                <span>{generateError}</span>
              </div>
            </div>
          {/if}

          {#if generationSuccess}
            <div class="alert alert-success">
              <Checkmark size={20} />
              <div class="alert-content">
                <strong>Success! Job #{generationSuccess.job?.id || generationSuccess.job?.__job_number__} Created!</strong>
                <span>The job and its material requirements have been saved to MongoDB Atlas and are ready for fleet dispatch.</span>
                <div class="alert-actions">
                  <button class="btn btn-sm btn-primary" on:click={goToActiveJobs} type="button">
                    <span>View in Active Jobs</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          {/if}

          {#if startJobSuccessNotice}
            <div class="alert alert-success start-job-banner" transition:slide>
              <Checkmark size={20} />
              <div class="alert-content">
                <strong>Success! Job #{startJobSuccessNotice.jobId} is now LIVE &amp; ACTIVE!</strong>
                <span>Operational commencement confirmed for <strong>{startJobSuccessNotice.startDate}</strong>.</span>
                <div class="dispatch-summary-chips">
                  <span class="dispatch-chip"><Email size={13} /> {startJobSuccessNotice.emailsCount} Emails queued</span>
                  <span class="dispatch-chip"><Phone size={13} /> {startJobSuccessNotice.smsCount} SMS queued</span>
                  <span class="dispatch-chip"><DeliveryTruck size={13} /> {startJobSuccessNotice.driversCount} Drivers queued</span>
                  <span class="dispatch-badge-placeholder">Messaging Service Integration Placeholder</span>
                </div>
              </div>
              <button class="banner-close-btn" type="button" on:click={() => (startJobSuccessNotice = null)} title="Dismiss notification">
                <Close size={16} />
              </button>
            </div>
          {/if}

          {#if deleteJobSuccessNotice}
            <div class="alert alert-danger" transition:slide>
              <TrashCan size={18} />
              <div class="alert-content">
                <strong>Job Deleted:</strong>
                <span>{deleteJobSuccessNotice}</span>
              </div>
              <button class="banner-close-btn" type="button" on:click={() => (deleteJobSuccessNotice = "")} title="Dismiss">
                <Close size={16} />
              </button>
            </div>
          {/if}


          <div class="workbench-body">
            <!-- 2-Column Details Grid -->
            <div class="details-grid">
              <!-- Site & Contact Summary -->
              <div class="info-card">
                <h3 class="card-section-title">Client &amp; Site Details</h3>
                <div class="info-table">
                  <div class="info-row">
                    <span class="info-label">Customer:</span>
                    <span class="info-value">{currentJob ? getJobCustomerName(currentJob) : getQuoteCustomerName(selectedQuote)}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Contact Person:</span>
                    <span class="info-value">{currentJob ? (currentJob.contacts?.[0]?.name || currentJob.contact?.name || selectedQuote?.customer?.contact_person || selectedQuote?.contact?.name || "N/A") : (selectedQuote?.customer?.contact_person || selectedQuote?.contact?.name || "N/A")}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Phone:</span>
                    <span class="info-value">{formatPhoneNumber(currentJob ? (currentJob.contacts?.[0]?.phone || (typeof currentJob.customer === 'object' ? currentJob.customer?.phone : "") || selectedQuote?.customer_summary?.phone || (typeof selectedQuote?.customer === 'object' ? selectedQuote?.customer?.phone : "")) : (selectedQuote?.customer_summary?.phone || (typeof selectedQuote?.customer === 'object' ? selectedQuote?.customer?.phone : ""))) || "N/A"}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Email:</span>
                    <span class="info-value">{currentJob ? (currentJob.contacts?.[0]?.email || currentJob.contact?.email || selectedQuote?.customer?.email || selectedQuote?.contact?.email || "N/A") : (selectedQuote?.customer?.email || selectedQuote?.contact?.email || "N/A")}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Pickup Site:</span>
                    <span class="info-value highlight-site">{currentJob ? getJobSiteAddress(currentJob) : getQuoteSiteAddress(selectedQuote)}</span>
                  </div>
                </div>
              </div>

              <!-- Job Dispatch Parameters -->
              <div class="info-card">
                <h3 class="card-section-title">Dispatch Parameters</h3>
                <div class="dispatch-form">
                  <div class="form-group">
                    <label class="form-label" for="job-status-select">Job Status on Creation:</label>
                    <select id="job-status-select" bind:value={jobStatus} class="form-select">
                      <option value="active">Active (Immediate Fleet Dispatch)</option>
                      <option value="proposed">Proposed (Draft Planning)</option>
                    </select>
                    <span class="form-hint">"Active" jobs appear immediately in the Active Jobs map &amp; cards.</span>
                  </div>

                  <div class="form-row-2">
                    <div class="form-group">
                      <label class="form-label" for="job-start-date">Scheduled Start Date:</label>
                      <div class="input-with-icon">
                        <Calendar size={16} class="input-icon" />
                        <input id="job-start-date" type="date" bind:value={startDate} class="form-input has-icon" />
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="form-label" for="po-number">PO / Ref Number:</label>
                      <input id="po-number" type="text" placeholder="e.g. PO-8841" bind:value={poNumber} class="form-input" />
                    </div>
                  </div>

                  <div class="form-row-2">
                    <div class="form-group">
                      <label class="form-label" for="load-uhf">Load UHF Channel:</label>
                      <input id="load-uhf" type="text" placeholder="e.g. Ch 29" bind:value={loadingUhf} class="form-input" />
                    </div>
                    <div class="form-group">
                      <label class="form-label" for="disp-uhf">Disposal UHF Channel:</label>
                      <input id="disp-uhf" type="text" placeholder="e.g. Ch 18" bind:value={disposalUhf} class="form-input" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Operational Job Summary Bar -->
            <div class="financial-summary-strip job-ops-strip">
              <div class="summary-metric">
                <span class="sm-label">Total Weight</span>
                <span class="sm-val">{formatTonnes(currentJob ? getJobTotalTonnes(currentJob) : getQuoteTotalTonnes(selectedQuote))}</span>
              </div>
              <div class="summary-metric">
                <span class="sm-label">Line Items</span>
                <span class="sm-val">{currentJob ? (currentJob.materials || []).length : (selectedQuote.quote_materials || []).length} Materials</span>
              </div>
              <div class="summary-metric">
                <span class="sm-label">Scheduled Start</span>
                <span class="sm-val">{currentJob?.start_date ? currentJob.start_date.split('T')[0] : startDate}</span>
              </div>
              <div class="summary-metric highlight">
                <span class="sm-label">Fleet Trucks Assigned</span>
                <span class="sm-val">{currentJob ? getJobTotalAllocatedTrucks(currentJob) : (selectedQuote.quote_materials || []).reduce((acc, m) => acc + (Array.isArray(m.allocated_trucks) ? m.allocated_trucks.length : 0), 0)} Trucks</span>
              </div>
            </div>

            <!-- Materials & Haulage Items -->
            <div class="materials-section">
              <div class="section-header">
                <h3 class="card-section-title">Job Material Specifications</h3>
                <span class="count-tag">{(selectedQuote.quote_materials || []).length} Line Item{(selectedQuote.quote_materials || []).length !== 1 ? 's' : ''}</span>
              </div>

              {#if (selectedQuote.quote_materials || []).length === 0}
                <div class="no-materials-box">
                  <p>No materials were attached to this job/quote.</p>
                </div>
              {:else}
                <div class="materials-table-wrapper">
                  <table class="materials-table">
                    <thead>
                      <tr>
                        <th>Material</th>
                        <th>Disposal Yard</th>
                        <th>Tonnes</th>
                        <th>Loads</th>
                        <th>Trucks / Runs</th>
                        <th>Unit Rate ($/t)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {#each selectedQuote.quote_materials as mat}
                        {@const tonnes = mat.total_tonnes || mat.quantity || 0}
                        {@const loads = Number(mat.schedule?.total_loads || Math.ceil(tonnes / (mat.fleet?.total_capacity_tonne || 38)))}
                        {@const trucks = Number(mat.schedule?.allocated_trucks || (Array.isArray(mat.allocated_trucks) ? mat.allocated_trucks.length : 1))}
                        {@const runs = Number(mat.schedule?.runs_per_day || 5)}
                        {@const unitPrice = mat.proposed_unit_price || mat.unit_price || (mat.financial_breakdown?.effective_rate_per_tonne) || 0}
                        {@const isSelectedMat = selectedMaterialForAllocation && (selectedMaterialForAllocation === mat || selectedMaterialForAllocation.material_name === mat.material_name || (selectedMaterialForAllocation.id && selectedMaterialForAllocation.id === mat.id))}
                        <tr 
                          class="clickable-mat-row {isSelectedMat ? 'active-mat-row' : ''}"
                          on:click={() => selectedMaterialForAllocation = mat}
                          title="Click to allocate trucks to {mat.material_name}"
                        >
                          <td>
                            <div class="mat-cell-interactive">
                              <strong class="mat-name-primary">{mat.material_name}</strong>
                            </div>
                          </td>
                          <td>
                            <div class="yard-cell">
                              <span>{mat.yard?.name || mat.dispose_address?.name || "Tipping Yard"}</span>
                              {#if mat.yard?.suburb || mat.dispose_address?.suburb}
                                <span class="suburb-tag">({mat.yard?.suburb || mat.dispose_address?.suburb})</span>
                              {/if}
                            </div>
                          </td>
                          <td><strong>{formatTonnes(tonnes)}</strong></td>
                          <td>{loads} loads</td>
                          <td>{trucks} truck{trucks > 1 ? 's' : ''} &bull; {runs} runs/pd</td>
                          <td>{unitPrice ? `${formatCurrency(unitPrice)}/t` : "—"}</td>
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              {/if}
            </div>

            <!-- Material Daily Fleet Allocation Board (with embedded Turnaround Schedule & Fleet Capacities) -->
            {#if selectedQuote}
              <TruckAllocationBoard 
                materials={selectedQuote.quote_materials || []}
                {startDate}
                onSaveAllocation={persistAllocationsToBackend}
                bind:isFleetDrawerOpen
                bind:fleetTrucks
                bind:selectedMaterial={selectedMaterialForAllocation}
                bind:allocations={currentAllocations}
                on:allocations-changed={handleAllocationsChanged}
                on:turnaround-change={handleTurnaroundScheduleChanged}
                on:material-change={handleMaterialChange}
              />
            {/if}

            <!-- Operational Notes -->
            <div class="notes-section">
              <label class="form-label" for="site-notes">Site &amp; Haulage Notes (included on Job materials):</label>
              <textarea
                id="site-notes"
                rows="2"
                class="form-textarea"
                placeholder="Enter access guidelines, tipping restrictions, or driver instructions..."
                bind:value={siteNotes}
              />
            </div>
          </div>

          <!-- Bottom Action Controls -->
          <div class="workbench-footer">
            <div class="footer-left">
              {#if currentJob}
                <div class="already-converted-notice">
                  <Checkmark size={16} />
                  <span>Editing active operational Job #{currentJob.__job_number__ || currentJob.id}. Changes update fleet allocations directly.</span>
                </div>
              {:else if isConverted}
                <div class="already-converted-notice">
                  <Checkmark size={16} />
                  <span>This quote has already been converted into an operational job.</span>
                </div>
              {:else}
                <span class="ready-hint">Pressing "Create Job" will persist the job and materials directly to MongoDB.</span>
              {/if}
            </div>

            <div class="footer-right">
              <button
                class="btn btn-danger"
                on:click={() => { deleteJobError = ""; isDeleteJobModalOpen = true; }}
                type="button"
                title="Permanently delete this job from the database"
              >
                <TrashCan size={16} />
                <span>Delete Job</span>
              </button>
              <button
                class="btn btn-primary btn-large"
                on:click={handleCreateJob}
                disabled={isGenerating}
                type="button"
              >
                {#if isGenerating}
                  <span class="btn-spinner" />
                  <span>{currentJob || isConverted ? "Saving Job..." : "Saving & Creating Job..."}</span>
                {:else}
                  <Save size={20} />
                  <span>{currentJob || isConverted ? "Save Job" : "Save & Create Job"}</span>
                {/if}
              </button>
            </div>
          </div>
        </div>
      {:else}
        <!-- Empty State When No Quote Selected -->
        <div class="empty-workbench">
          <div class="empty-card">
            <div class="empty-icon-wrap">
              <DeliveryTruck size={48} />
            </div>
            <h2 class="empty-title">
              Select a Job to Edit or Allocate Fleet
            </h2>
            <p class="empty-desc">
              Choose an active, pending, or proposed job from the sidebar to review its site details, update radio UHF channels, and configure daily fleet truck allocations.
            </p>
            <div class="empty-actions">
              <button class="btn btn-primary" on:click={() => (creationMode = 'manual')} type="button">
                <Add size={16} />
                <span>Create Job Manually</span>
              </button>
              {#if !isSidebarOpen}
                <button class="btn btn-outline" on:click={() => (isSidebarOpen = true)} type="button">
                  <SidePanelOpen size={16} />
                  <span>Show Jobs List</span>
                </button>
              {/if}
              <button class="btn btn-outline" on:click={goToQuotesAndRoutes} type="button">
                <DocumentAdd size={16} />
                <span>Go to Quotes &amp; Routes</span>
              </button>
            </div>
          </div>
        </div>
      {/if}
    </main>
  </div>

  <StartJobModal
    isOpen={isStartJobModalOpen}
    {currentJob}
    {selectedQuote}
    {currentAllocations}
    initialStartDate={startDate}
    {isStartingJob}
    {startJobError}
    {cachedCompanies}
    {cachedYards}
    on:close={() => (isStartJobModalOpen = false)}
    on:confirm={handleStartJobConfirmed}
  />

  <DeleteJobModal
    isOpen={isDeleteJobModalOpen}
    job={currentJob || matchingConvertedJob}
    quote={selectedQuote}
    isDeleting={isDeletingJob}
    errorMessage={deleteJobError}
    on:close={() => (isDeleteJobModalOpen = false)}
    on:confirm={handleConfirmDeleteJob}
  />
</div>

<style>
  .create-job-page {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background-color: var(--tt-slate-50, #f8fafc);
    color: var(--tt-slate-900, #0f172a);
    overflow: hidden;
  }

  /* Page Header */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.9rem 1.75rem;
    background: #ffffff;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    z-index: 10;
  }

  .header-badge-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.2rem;
  }

  .page-badge {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.15rem 0.5rem;
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-700, #334155);
    border-radius: 4px;
  }

  .step-badge {
    font-size: 0.72rem;
    font-weight: 600;
    padding: 0.15rem 0.5rem;
    background: #eff6ff;
    color: #1d4ed8;
    border-radius: 4px;
  }

  .page-title {
    font-size: 1.35rem;
    font-weight: 700;
    margin: 0;
    color: var(--tt-slate-900, #0f172a);
  }

  .page-subtitle {
    font-size: 0.82rem;
    color: var(--tt-slate-500, #64748b);
    margin: 0.15rem 0 0 0;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  /* Fleet Trucks Pullout Tab: Pinned to the top right, directly under the header */
  .fleet-pullout-tab {
    position: absolute;
    top: 1rem;
    right: 0;
    width: 25px;
    height: 52px;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-right: none;
    border-radius: 8px 0 0 8px;
    box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    cursor: pointer;
    z-index: 1250;
    color: var(--tt-slate-600, #475569);
    transition: right 0.28s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s ease, color 0.15s ease, width 0.15s ease, box-shadow 0.15s ease;
    padding: 0;
    user-select: none;
  }

  .fleet-pullout-tab.is-open {
    right: 420px;
  }

  @media (max-width: 456px) {
    .fleet-pullout-tab.is-open {
      right: 92vw;
    }
  }

  .fleet-pullout-tab.is-closed {
    right: 0;
  }

  .fleet-pullout-tab:hover {
    background: #eff6ff;
    border-color: #93c5fd;
    color: var(--tt-primary, #2563eb);
    width: 28px;
    box-shadow: -3px 2px 10px rgba(37, 99, 235, 0.15);
  }

  :global(.fleet-pullout-icon) {
    color: var(--tt-primary, #2563eb);
    flex-shrink: 0;
    transition: transform 0.15s ease;
  }

  .fleet-pullout-tab:hover :global(.fleet-pullout-icon) {
    transform: scale(1.08);
  }

  .fleet-pullout-tab:hover :global(.tab-chevron) {
    color: var(--tt-primary, #2563eb);
  }

  /* Main Split Studio Layout */
  /* Main Split Studio Layout */
  .studio-layout {
    display: flex;
    flex: 1;
    overflow: hidden;
    position: relative;
  }


  /* Edge Slider Handle */
  .sidebar-edge-tab {
    position: absolute;
    top: 1rem;
    width: 25px;
    height: 52px;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-left: none;
    border-radius: 0 8px 8px 0;
    box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    cursor: pointer;
    z-index: 25;
    color: var(--tt-slate-600, #475569);
    transition: left 0.28s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s ease, color 0.15s ease, width 0.15s ease, box-shadow 0.15s ease;
    padding: 0;
  }

  .sidebar-edge-tab.is-open {
    left: 380px;
  }

  .sidebar-edge-tab.is-closed {
    left: 0;
  }

  .sidebar-edge-tab:hover {
    background: #eff6ff;
    color: var(--tt-primary, #2563eb);
    border-color: #93c5fd;
    width: 28px;
    box-shadow: 3px 2px 10px rgba(37, 99, 235, 0.15);
  }

  :global(.clipboard-tab-icon) {
    color: var(--tt-primary, #2563eb);
    flex-shrink: 0;
    transition: transform 0.15s ease;
  }

  .sidebar-edge-tab:hover :global(.clipboard-tab-icon) {
    transform: scale(1.08);
  }

  :global(.tab-chevron) {
    color: var(--tt-slate-500, #64748b);
    flex-shrink: 0;
    transition: color 0.15s ease;
  }

  .sidebar-edge-tab:hover :global(.tab-chevron) {
    color: var(--tt-primary, #2563eb);
  }


  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.45rem;
    border-radius: 4px;
  }

  .status-draft {
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-700, #334155);
  }

  .status-converted {
    background: #dcfce7;
    color: #15803d;
  }

  .status-active {
    background: #dcfce7;
    color: #15803d;
  }

  .status-proposed {
    background: #e0f2fe;
    color: #0369a1;
  }

  .status-pending {
    background: #fef3c7;
    color: #b45309;
  }

  .status-completed {
    background: #f1f5f9;
    color: #475569;
  }

  .status-cancelled {
    background: #fee2e2;
    color: #b91c1c;
  }

  /* Right Workbench */
  .workbench-main {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem 1.75rem;
    background: #f8fafc;
  }

  .workbench-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .workbench-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    background: #ffffff;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .workbench-header-actions {
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  .header-tags {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.35rem;
  }

  .quote-id-large {
    font-size: 0.85rem;
    font-weight: 800;
    background: #eff6ff;
    color: #1d4ed8;
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    font-family: monospace;
  }

  .quote-code-tag {
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--tt-slate-600, #475569);
  }

  .workbench-client-title {
    font-size: 1.45rem;
    font-weight: 800;
    margin: 0 0 0.2rem 0;
    color: var(--tt-slate-900, #0f172a);
  }

  .workbench-date {
    font-size: 0.8rem;
    color: var(--tt-slate-500, #64748b);
  }

  .workbench-body {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
  }

  .info-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 1.1rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  }

  .card-section-title {
    font-size: 0.92rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--tt-slate-700, #334155);
    margin: 0 0 0.85rem 0;
    padding-bottom: 0.45rem;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .info-table {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    font-size: 0.84rem;
  }

  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .info-label {
    color: var(--tt-slate-500, #64748b);
    font-weight: 500;
    min-width: 100px;
  }

  .info-value {
    color: var(--tt-slate-800, #1e293b);
    font-weight: 600;
    text-align: right;
  }

  .highlight-site {
    color: #1e40af;
    max-width: 250px;
    word-break: break-word;
  }

  /* Dispatch Form */
  .dispatch-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .form-row-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }

  .form-label {
    font-size: 0.76rem;
    font-weight: 600;
    color: var(--tt-slate-700, #334155);
  }

  .form-select, .form-input, .form-textarea {
    width: 100%;
    padding: 0.45rem 0.65rem;
    font-size: 0.82rem;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    background: #ffffff;
    color: var(--tt-slate-900, #0f172a);
    outline: none;
  }

  .form-select:focus, .form-input:focus, .form-textarea:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .form-hint {
    font-size: 0.7rem;
    color: var(--tt-slate-500, #64748b);
  }

  .input-with-icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  :global(.input-icon) {
    position: absolute;
    left: 0.6rem;
    color: var(--tt-slate-400, #94a3b8);
    pointer-events: none;
  }

  .has-icon {
    padding-left: 2.1rem;
  }

  /* Materials Section */
  .materials-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .count-tag {
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--tt-slate-500, #64748b);
  }

  .turnaround-panel-container {
    margin: 1.25rem 0;
  }

  .materials-table-wrapper {
    overflow-x: auto;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
  }

  .materials-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.82rem;
  }

  .materials-table th {
    background: #f8fafc;
    padding: 0.65rem 0.85rem;
    font-weight: 700;
    text-align: left;
    color: var(--tt-slate-600, #475569);
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .materials-table td {
    padding: 0.75rem 0.85rem;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-800, #1e293b);
  }

  .clickable-mat-row {
    cursor: pointer;
    transition: background 0.15s ease, box-shadow 0.15s ease;
  }

  .clickable-mat-row:hover {
    background-color: #f1f5f9;
  }

  .clickable-mat-row.active-mat-row {
    background-color: #eff6ff !important;
    box-shadow: inset 4px 0 0 #2563eb;
  }

  .clickable-mat-row.active-mat-row td {
    color: #1e3a8a;
  }

  .mat-cell-interactive {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }



  .mat-name-primary {
    color: #1e3a8a;
  }

  .yard-cell {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    flex-wrap: wrap;
  }

  .suburb-tag {
    font-size: 0.75rem;
    color: var(--tt-slate-500, #64748b);
  }

  .cheapest-badge {
    font-size: 0.68rem;
    font-weight: 700;
    background: #dcfce7;
    color: #15803d;
    padding: 0.1rem 0.35rem;
    border-radius: 3px;
  }

  .price-val {
    color: #0f766e;
  }

  /* Financial Strip */
  .financial-summary-strip {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    background: #f1f5f9;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 0.85rem 1.25rem;
    gap: 1rem;
  }

  .summary-metric {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .sm-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-500, #64748b);
  }

  .sm-val {
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
  }

  .summary-metric.highlight .sm-val {
    color: #0d9488;
    font-weight: 800;
  }

  /* Workbench Footer */
  .workbench-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.1rem 1.5rem;
    background: #ffffff;
    border-top: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .ready-hint {
    font-size: 0.8rem;
    color: var(--tt-slate-500, #64748b);
  }

  .already-converted-notice {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: #15803d;
  }

  .footer-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  /* Alerts */
  .alert {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    margin: 1rem 1.5rem 0 1.5rem;
    border-radius: 8px;
    font-size: 0.85rem;
  }

  .alert-content {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .alert-actions {
    margin-top: 0.45rem;
  }

  .alert-danger {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
  }

  .alert-success {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
  }

  /* Empty Workbench */
  .empty-workbench {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 480px;
  }

  .empty-card {
    text-align: center;
    max-width: 440px;
    background: #ffffff;
    padding: 2.5rem 2rem;
    border-radius: 12px;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  }

  .empty-icon-wrap {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: #eff6ff;
    color: #2563eb;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.25rem auto;
  }

  .empty-title {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
  }

  .empty-desc {
    font-size: 0.85rem;
    color: var(--tt-slate-500, #64748b);
    line-height: 1.45;
    margin: 0 0 1.5rem 0;
  }

  /* Buttons */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    font-size: 0.82rem;
    font-weight: 600;
    padding: 0.5rem 0.9rem;
    border-radius: 6px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-sm {
    padding: 0.35rem 0.7rem;
    font-size: 0.78rem;
  }

  .btn-xs {
    padding: 0.2rem 0.5rem;
    font-size: 0.72rem;
  }

  .btn-large {
    padding: 0.65rem 1.4rem;
    font-size: 0.95rem;
    font-weight: 700;
  }

  .btn-primary {
    background: #2563eb;
    color: #ffffff;
    border-color: #1d4ed8;
  }

  .btn-primary:hover:not(:disabled) {
    background: #1d4ed8;
  }

  .btn-outline {
    background: #ffffff;
    border-color: var(--tt-slate-300, #cbd5e1);
    color: var(--tt-slate-700, #334155);
  }

  .btn-outline:hover {
    background: var(--tt-slate-50, #f8fafc);
    border-color: var(--tt-slate-400, #94a3b8);
  }

  .btn-ghost {
    background: transparent;
    color: var(--tt-slate-600, #475569);
  }

  .btn-ghost:hover {
    background: var(--tt-slate-100, #f1f5f9);
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  .spinner {
    width: 24px;
    height: 24px;
    border: 3px solid var(--tt-slate-200, #e2e8f0);
    border-top-color: #2563eb;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
    margin: 0 auto 0.5rem auto;
  }

  .loading-state, .error-state, .empty-list-state {
    text-align: center;
    padding: 2rem 1rem;
    font-size: 0.82rem;
    color: var(--tt-slate-500, #64748b);
  }

  :global(.spin-icon) {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Mode Toggle in Header */
  .mode-toggle-group {
    display: flex;
    align-items: center;
    background-color: var(--tt-slate-100, #f1f5f9);
    border-radius: 8px;
    padding: 3px;
    gap: 3px;
  }

  .mode-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--tt-slate-600, #475569);
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .mode-toggle-btn:hover {
    color: var(--tt-slate-900, #0f172a);
  }

  .mode-toggle-btn.active {
    background-color: #ffffff;
    color: var(--tt-primary, #2563eb);
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  /* Manual Workbench Styles */
  .manual-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
  }

  .manual-form-container {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .manual-section {
    background-color: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 10px;
    padding: 1.25rem;
  }

  .manual-sec-title {
    font-size: 13.5px;
    font-weight: 700;
    color: var(--tt-slate-800, #1e293b);
    margin: 0 0 1rem 0;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .manual-grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 12px;
  }

  .grid-col {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .grid-col.col-12 { grid-column: span 12; }
  .grid-col.col-8 { grid-column: span 8; }
  .grid-col.col-6 { grid-column: span 6; }
  .grid-col.col-4 { grid-column: span 4; }

  .manual-submit-row {
    display: flex;
    justify-content: flex-end;
    padding-top: 0.5rem;
  }

  .btn-large {
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
  }

  @media (max-width: 900px) {
    .studio-layout {
      flex-direction: column;
    }
    .quotes-sidebar {
      width: 100%;
      max-width: 100%;
      height: 260px;
    }
    .details-grid {
      grid-template-columns: 1fr;
    }
    .financial-summary-strip {
      grid-template-columns: 1fr 1fr;
    }
  }

  /* Job Status Dropdown Control in Workbench Header */
  .job-status-control {
    display: inline-flex;
    align-items: center;
    position: relative;
    gap: 0.45rem;
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    padding: 0 0.5rem 0 0.65rem;
    height: 31px;
    transition: all 0.15s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .job-status-control:hover {
    border-color: var(--tt-slate-400, #94a3b8);
  }

  .job-status-control:focus-within {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .status-indicator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .dot-proposed { background-color: #d97706; }
  .dot-pending { background-color: #f59e0b; }
  .dot-active { background-color: #16a34a; box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.2); }
  .dot-completed { background-color: #2563eb; }
  .dot-cancelled { background-color: #dc2626; }

  .job-header-status-select {
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    cursor: pointer;
    color: var(--tt-slate-700, #334155);
    padding: 0.2rem 0.1rem;
  }

  .status-select-proposed { color: #b45309; }
  .status-select-pending { color: #d97706; }
  .status-select-active { color: #15803d; }
  .status-select-completed { color: #1d4ed8; }
  .status-select-cancelled { color: #b91c1c; }

  .status-spinner-xs {
    width: 12px;
    height: 12px;
    border: 2px solid var(--tt-slate-300, #cbd5e1);
    border-top-color: #2563eb;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
    flex-shrink: 0;
  }

  /* START JOB Button & Modal Styles */
  .btn-start-job {
    background: #16a34a;
    color: #ffffff;
    border: 1px solid #15803d;
    font-weight: 700;
    letter-spacing: 0.02em;
    box-shadow: 0 1px 3px rgba(22, 163, 74, 0.25);
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    transition: all 0.15s ease;
  }

  .btn-start-job:hover:not(:disabled) {
    background: #15803d;
    border-color: #166534;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(22, 163, 74, 0.35);
  }

  .btn-start-job:active:not(:disabled) {
    transform: translateY(0);
  }

  /* Start Job Live Banner */
  .start-job-banner {
    position: relative;
    padding-right: 2.5rem;
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #166534;
  }

  .dispatch-summary-chips {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-top: 0.5rem;
  }

  .dispatch-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.76rem;
    font-weight: 600;
    background: #ffffff;
    border: 1px solid #bbf7d0;
    color: #15803d;
    padding: 0.2rem 0.55rem;
    border-radius: 9999px;
  }

  .dispatch-badge-placeholder {
    font-size: 0.72rem;
    font-weight: 600;
    background: #fef3c7;
    border: 1px solid #fde68a;
    color: #b45309;
    padding: 0.2rem 0.55rem;
    border-radius: 9999px;
  }

  .banner-close-btn {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    background: transparent;
    border: none;
    color: #15803d;
    cursor: pointer;
    padding: 2px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.7;
    transition: opacity 0.15s ease;
  }

  .banner-close-btn:hover {
    opacity: 1;
    background: rgba(22, 163, 74, 0.1);
  }


  /* Delete Job Button & Modal Styles */
  .btn-danger {
    background: #dc2626;
    color: #ffffff;
    border: 1px solid #b91c1c;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.88rem;
    transition: all 0.15s ease;
  }

  .btn-danger:hover:not(:disabled) {
    background: #b91c1c;
    border-color: #991b1b;
    box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3);
  }

  .btn-danger:active:not(:disabled) {
    transform: translateY(1px);
  }

  .btn-danger:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
</style>

